import { Grid, Modal } from "antd"
import React from "react"

import type { WidgetInstance, WidgetPayload } from "./types"
import { WidgetRenderer } from "./WidgetRenderer"

interface WidgetExpandModalProps {
  widget: WidgetInstance | null
  payload: WidgetPayload | null
  currency?: string
  onClose: () => void
}

export const WidgetExpandModal = ({
  widget,
  payload,
  currency,
  onClose
}: WidgetExpandModalProps) => {
  const screens = Grid.useBreakpoint()
  const fullScreen = !screens.md

  return (
    <Modal
      open={widget !== null}
      onCancel={onClose}
      footer={null}
      centered={!fullScreen}
      // recharts sizes the chart from its measured box, which the zoom-in scale
      // transform distorts — it would re-render on every frame of the animation.
      transitionName=""
      width={fullScreen ? "100vw" : "90vw"}
      style={
        fullScreen ? { top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 } : undefined
      }
      title={widget?.config?.title ?? widget?.title}
      destroyOnHidden
      styles={{
        content: fullScreen ? { borderRadius: 0, minHeight: "100dvh" } : undefined,
        body: {
          height: fullScreen ? "calc(100dvh - 96px)" : "80vh",
          // A chart fills this box, so letting it scroll is a feedback loop: one
          // rounded-up pixel shows a scrollbar, which shrinks the box, which makes
          // recharts re-measure and restart its entry animation, which frees the
          // pixel again. Clip instead so the chart's size cannot move.
          overflow: widget?.type === "chart" ? "hidden" : "auto"
        }
      }}
    >
      {widget && (
        <div data-testid="widget-expand-modal" style={{ height: "100%" }}>
          <WidgetRenderer
            widget={{
              ...widget,
              title: undefined,
              capabilities: [],
              config: widget.config
                ? { ...widget.config, title: undefined, limit: undefined }
                : undefined
            }}
            payload={payload}
            state="ready"
            currency={currency}
            fillHeight
          />
        </div>
      )}
    </Modal>
  )
}
